import { useQuery } from '@apollo/client/react';
import { DimeButton, DimeCardShellSkeleton, DimeDetailPageStates } from '@nudool/design-system';
import { isValidUuidV7 } from '@nudool/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { WarehouseIcon } from 'lucide-react';
import { useBreadcrumbLabel } from '../../../components/BreadcrumbLabelsContext.js';
import { usePermissions } from '../../../modules/iam/permissions-context.js';
import { LocationGroupDetailView } from '../../../modules/location-groups/location-group-detail.js';
import {
  GET_LOCATION_GROUP,
  type GetLocationGroupQueryResult,
  type GetLocationGroupQueryVariables,
} from '../../../modules/location-groups/operations.js';

export const Route = createFileRoute('/warehouses/$locationGroupId/')({
  component: WarehouseDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function WarehouseDetailPage() {
  const navigate = useNavigate();
  const { locationGroupId } = Route.useParams();
  const { permissions } = usePermissions();
  const { data, loading, error, refetch } = useQuery<
    GetLocationGroupQueryResult,
    GetLocationGroupQueryVariables
  >(GET_LOCATION_GROUP, {
    variables: {
      id: locationGroupId,
      canEditLocationGroups: permissions.includes('locationGroup:edit'),
      canViewUsers: permissions.includes('user:view'),
    },
    // The API refuses an id that isn't one as bad input, so no warehouse can match it.
    skip: !isValidUuidV7(locationGroupId),
  });

  const locationGroup = data?.locationGroup;
  useBreadcrumbLabel(locationGroupId, locationGroup?.name);

  return (
    <DimeDetailPageStates
      data={locationGroup}
      loading={loading}
      error={error}
      entityLabel="Warehouse"
      notFoundIcon={<WarehouseIcon />}
      notFoundAction={
        <DimeButton variant="outline" asChild>
          <Link to="/warehouses">Back to warehouses</Link>
        </DimeButton>
      }
      skeleton={<DimeCardShellSkeleton factCount={3} actionCount={3} tabCount={3} rowCount={5} />}
      onRetry={() => void refetch().catch(() => undefined)}
    >
      {(warehouse) => (
        <LocationGroupDetailView
          key={warehouse.id}
          locationGroup={warehouse}
          onChanged={() => void refetch()}
          onNewLocation={() =>
            void navigate({ to: '/warehouses/$locationGroupId/new', params: { locationGroupId } })
          }
        />
      )}
    </DimeDetailPageStates>
  );
}
